import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ReactNode, useEffect, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { Helmet } from "react-helmet";

import loadingGif from "assets/gif/loading.gif";

import {
  fetchDatasets,
  fetchSortedDatasets,
  fetchSortedDatasetsLongPoll,
  setEnrichingDataset,
  setFilterData,
} from "redux/Datasets/actions";
import {
  getContentLoading,
  getDatasets,
  getDeleteModalId,
  getEnrichingDataset,
  getFilterData,
  getSampleData,
} from "redux/Datasets/selectors";
import { getUserInfo } from "redux/User/selectors";
import { State } from "redux/store";
import { isAnonymousUser, selectUser, userLogout } from "redux/authReducer";
import {
  getShowDSCreationWarning,
  getShowRequestDemoModal,
} from "redux/Modals/selectors";
import { setShowDSCreationWarning } from "redux/Modals/actions";

import {
  calculatePlaceHolderCount,
  useWindowResize,
} from "helpers/utility/utilities";
import { amplitudeTrack } from "helpers/utility/amplitude";
import { PRIVATE_DATASETS_PATH } from "helpers/constants/paths";
import { AMP_DATASET_OPENED } from "helpers/constants/amplitudeEvents";
import {
  AMP_DATASET_ID,
  AMP_DATASET_NAME,
  AMP_DATASET_SOURCE,
} from "helpers/constants/amplitudeProperties";
import {
  ContactUsComponentType,
  Dataset,
  DatasetsFilterValue,
  DatasetSortEnum,
  DatasetTypeEnum,
  DatasetStatus,
  EnrichingDS,
  InventoryFilterData,
} from "types";
import { REVIEW_PARAMETER } from "helpers/constants/miscellaneous";

import Header from "views/layout/Header";
import ContentLoad from "views/uikit/ContentLoad";

import InternalServerErrorModal from "views/modals/InternalServerErrorModal";
import { NoMatchFoundCard } from "views/components/NoMatchFoundCard";
import CreateDatasetModal from "views/modals/CreateDatasetModal";
import SignupSuccessModal from "views/modals/SignupSuccessModal";
import DatasetErrorModal from "views/modals/DatasetErrorModal";
import LimitExceedModal from "views/modals/LimitExceedModal";
import UpgradePlanModal from "views/modals/UpgradePlanModal";
import RequestDemoModal from "views/modals/RequestDemoModal";
import FreePlanModal from "views/modals/FreePlanModal";
import ProPlanModal from "views/modals/ProPlanModal";
import SignupModal from "views/modals/SignupModal";
import ErrorModal from "views/modals/ErrorModal";
import DatasetsFilters from "./DatasetsFilters";

import AddDatasetCard from "./AddDatasetCard";
import DatasetCard from "./DatasetCard";

import styles from "./style.module.scss";

import { toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import intersection from "lodash/intersection";
import { setSeverityData } from "redux/SingleDataset/actions";
import { useUserConfig } from "../../../hooks/useUserConfig";

const STATUS_ORDER: Record<string, number> = {
  [DatasetStatus.INDEXING]: 0,
  [DatasetStatus.DRAFT]: 4,
  [DatasetStatus.READY]: 3,
  [DatasetStatus.READ_ONLY]: 1,
  [DatasetStatus.PARTIAL_INDEX]: 2,
  [DatasetStatus.ERROR]: 5,
};

const DatasetInventory = ({
  datasets,
  filterData,
  deleteModalId,
  isContentLoading,
  showRequestDemoModal,
  userId,
  enrichingDataset,
  showDSCreationWarning,
}: DatasetInventoryTypes) => {
  const [filteredDatasets, setFilteredDatasets] = useState(datasets);

  const [dropdownBoxDisplay, setdropdownBoxDisplay] = useState(false);
  const [count, setCount] = useState(0);
  const [showDatasetTypeDropdown, setShowDatasetTypeDropdown] = useState(false);
  const [cardOffsetWidth, setCardOffsetWidth] = useState(0);

  const cardContainer = useRef<HTMLDivElement>(null);
  const cardElement = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const filterOptionsRef = useRef<HTMLDivElement>(null);
  const optionsDropdownButtonRef = useRef<HTMLDivElement>(null);
  const previousResultRef = useRef<any>([]);
  const toastIdRef = useRef<any>(null);
  const [searchParams] = useSearchParams();
  const inReview = searchParams.get(REVIEW_PARAMETER);

  const {
    workspaces,
    config: userConfig,
    isLoading: isUserConfigLoading,
  } = useUserConfig();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pathName = useLocation().pathname;
  const { height, width } = useWindowResize();

  const user = selectUser();

  const dispatchFilterData = (data: InventoryFilterData) => {
    dispatch(setFilterData(data));
  };

  const showToast = (Element: ReactNode) => {
    const DEFAULT_TOAST_SETTINGS: any = {
      autoClose: false,
      closeButton: false,
      closeOnClick: true,
      position: "bottom-center",
    };

    if (toastIdRef.current) {
      toast.update(toastIdRef.current, {
        ...DEFAULT_TOAST_SETTINGS,
        render: Element,
      });
    } else {
      toastIdRef.current = toast(Element, DEFAULT_TOAST_SETTINGS);
    }
  };

  const handleToastClose = () => {
    toast.dismiss(toastIdRef.current);
    toastIdRef.current = null;
  };

  const handleShowOnPremGuide = () => {
    window.open(
      "https://docs.visual-layer.com/docs/on-prem/operation",
      "_blank",
    );
    dispatch(setShowDSCreationWarning(false));
  };

  const isPrivatePath = pathName === PRIVATE_DATASETS_PATH || pathName === "/";

  const dynamicTitle = isPrivatePath
    ? "Dataset Inventory"
    : "Public VL Datasets";

  const isAnyFilterSelected =
    filterData.searchValue ||
    filterData.sortValue !== DatasetSortEnum.STATUS ||
    filterData.datasetType !== DatasetTypeEnum.All ||
    [
      ...filterData.datasetStatus,
      ...filterData.datasetJob,
      ...filterData.datasetSetup,
      ...filterData.datasetRecipe,
    ].filter((status) => status.selected).length > 0;

  const length = filteredDatasets.length;

  const datasetsCountText = `Displaying ${
    datasets.length === filteredDatasets.length ? "All" : "Filtered"
  } Datasets (${filteredDatasets.length}/${datasets.length})`;

  const handleOutsideClick = (event: any) => {
    const optionDropdownButton = optionsDropdownButtonRef.current;
    const options = filterOptionsRef.current;

    const clickedInOptions = options && !options.contains(event.target);
    const clickedOnOptionDropdown =
      optionDropdownButton && !optionDropdownButton.contains(event.target);
    if (
      showDatasetTypeDropdown &&
      clickedInOptions &&
      clickedOnOptionDropdown
    ) {
      setShowDatasetTypeDropdown(false);
    }

    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setdropdownBoxDisplay(false);
    }
  };

  const renderInventoryInfo = () => {
    return (
      <DatasetsFilters
        filterData={filterData}
        setFilterData={dispatchFilterData}
        datasetsCountText={datasetsCountText}
        handleClearFilters={handleClearFilters}
      />
    );
  };

  const handleClearFilters = () => {
    const unselectOptions = (options: DatasetsFilterValue[]) =>
      options.map((option) => ({ ...option, selected: false }));

    dispatchFilterData({
      datasetStatus: unselectOptions(filterData.datasetStatus),
      datasetJob: unselectOptions(filterData.datasetJob),
      datasetSetup: unselectOptions(filterData.datasetSetup),
      datasetRecipe: unselectOptions(filterData.datasetRecipe),
      datasetType: DatasetTypeEnum.All,
      sortValue: DatasetSortEnum.STATUS,
      searchValue: "",
    });
    setFilteredDatasets(datasets);
  };

  const setCardWitdh = (value: number) => {
    setCardOffsetWidth(value);
  };

  useEffect(() => {
    let result = [...datasets];
    const {
      datasetStatus,
      datasetType,
      searchValue,
      sortValue,
      datasetJob,
      datasetSetup,
      datasetRecipe,
    } = filterData;

    const getSelectedValuesIds = (values: DatasetsFilterValue[]) =>
      values.filter((status) => status.selected).map((status) => status.id);

    const selectedStatuses = getSelectedValuesIds(datasetStatus);
    const selectedJobs = getSelectedValuesIds(datasetJob);
    const selectedSetups = getSelectedValuesIds(datasetSetup);
    const selectedRecipes = getSelectedValuesIds(datasetRecipe);

    if (selectedStatuses.length > 0) {
      result = result.filter((dataset) =>
        selectedStatuses.includes(dataset.status),
      );
    }

    if (selectedJobs.length > 0) {
      result = result.filter((dataset) =>
        selectedJobs.includes(
          dataset.metadata?.camtek_db_export?.["Job Name"] as string,
        ),
      );
    }

    if (selectedSetups.length > 0) {
      result = result.filter((dataset) =>
        selectedSetups.includes(
          dataset.metadata?.camtek_db_export?.["Setup Name"] as string,
        ),
      );
    }

    if (selectedRecipes.length > 0) {
      result = result.filter(
        (dataset) =>
          intersection(
            selectedRecipes,
            dataset.metadata?.camtek_db_export?.["Recipes Name"] as string[],
          ).length > 0,
      );
    }

    if (searchValue) {
      result = result.filter((dataset) => {
        const datasetName: string = dataset.display_name.toLowerCase();
        const searchText = searchValue.toLowerCase();

        return datasetName.includes(searchText) || false;
      });
    }

    if (datasetType === DatasetTypeEnum.Private) {
      result = result.filter((dataset) => !dataset.sample);
    } else if (datasetType === DatasetTypeEnum.Public) {
      result = result.filter((dataset) => dataset.sample);
    }

    switch (sortValue) {
      case DatasetSortEnum.SIZE:
        result = result.sort(
          (datasetA, datasetB) => datasetB.size_bytes - datasetA.size_bytes,
        );
        break;

      case DatasetSortEnum.NAME:
        result = result.sort((datasetA, datasetB) => {
          return datasetA.display_name.localeCompare(datasetB.display_name);
        });
        break;

      case DatasetSortEnum.DATE:
        result = result.sort(
          (datasetA, datasetB) =>
            new Date(datasetB.created_at).getTime() -
            new Date(datasetA.created_at).getTime(),
        );
        break;

      case DatasetSortEnum.STATUS:
        result = result.sort((datasetA, datasetB) => {
          const statusA = STATUS_ORDER[datasetA.status];
          const statusB = STATUS_ORDER[datasetB.status];

          if (statusA !== statusB) {
            return statusA - statusB;
          }

          return (
            new Date(datasetB.created_at).getTime() -
            new Date(datasetA.created_at).getTime()
          );
        });

        break;
    }

    if (JSON.stringify(result) !== JSON.stringify(previousResultRef.current)) {
      setFilteredDatasets(result);
      previousResultRef.current = result;
    }
  }, [filterData, datasets]);

  useEffect(() => {
    const extraWidth = 60;
    const recordsCount = isPrivatePath ? length + 1 : length;
    const card = cardElement.current
      ? cardElement.current
      : length > 0
        ? { offsetWidth: cardOffsetWidth }
        : null;

    let tempCount = calculatePlaceHolderCount(
      cardContainer.current,
      card, //in case the ref is null, try using cardOffsetWidth
      recordsCount,
      extraWidth,
    );
    if (count !== tempCount) {
      setCount(tempCount);
    }
    //eslint-disable-next-line
  }, [height, width, count, length, datasets, filterData, cardOffsetWidth]);

  useEffect(() => {
    let cancelled = false;
    const abortController = new AbortController();
    let checkUserConfigInterval: any;
    const isUserConfigReady = () =>
      !isUserConfigLoading && userConfig && userConfig.length > 0;

    if (
      isPrivatePath &&
      process.env.REACT_APP_IS_AUTHENTICATION_DISABELED === "false" &&
      ((user && isAnonymousUser()) || !user)
    ) {
      userLogout();
    }

    function startFetchingDatasets() {
      // Initial fetch
      dispatch(fetchSortedDatasets(true, filterData.sortValue) as any);
      // Long-poll loop for subsequent updates
      (async () => {
        let retryDelay = 5000;
        while (!cancelled) {
          try {
            await dispatch(
              fetchSortedDatasetsLongPoll(
                isPrivatePath,
                filterData.sortValue,
                true,
                abortController.signal,
              ) as any,
            );
            retryDelay = 5000;
          } catch {
            if (cancelled) break;
            const delay = retryDelay;
            await new Promise((r) => setTimeout(r, delay));
            retryDelay = Math.min(retryDelay * 2, 30000);
          }
        }
      })();
    }

    // Check if userConfig is initially filled, if not, check again
    if (!isUserConfigReady()) {
      checkUserConfigInterval = setInterval(() => {
        if (isUserConfigReady()) {
          clearInterval(checkUserConfigInterval);
          startFetchingDatasets();
        }
      }, 300);
    } else {
      startFetchingDatasets();
    }

    return () => {
      cancelled = true;
      abortController.abort();
      clearInterval(checkUserConfigInterval);
    };
    //eslint-disable-next-line
  }, [dispatch, isPrivatePath, userConfig, isUserConfigLoading]);

  useEffect(() => {
    if (!enrichingDataset) {
      if (toastIdRef.current) {
        toast.dismiss(toastIdRef.current);
      }
      return;
    }

    const { progress, type, name } = enrichingDataset;

    switch (type) {
      case "IN_PROGRESS":
        showToast(
          <VLToastComponent
            title={`Enrichment in progress for ${name}`}
            icon={loadingGif}
            progress={progress}
            status="IN_PROGRESS"
            onClose={handleToastClose}
            description={
              "We have started enriching your dataset. You will be notified once it's ready"
            }
          />,
        );
        return;

      case "SUCCESS":
        showToast(
          <VLToastComponent
            status="SUCCESS"
            onClose={() => {
              handleToastClose();
              dispatch(setEnrichingDataset(null));
            }}
            title={`Enrichment is completed for ${name}`}
            description={"Your dataset enrichment completed successfully"}
          />,
        );

        return;
    }
    //eslint-disable-next-line
  }, [enrichingDataset]);

  const LoadDataset = (
    datasetId: string,
    datasetName: string,
    datasetSource: string,
  ) => {
    amplitudeTrack(AMP_DATASET_OPENED, {
      [AMP_DATASET_NAME]: datasetName,
      [AMP_DATASET_ID]: datasetId,
      [AMP_DATASET_SOURCE]: datasetSource,
    });
    dispatch(setSeverityData(null));
    navigate(`/dataset/${datasetId}/data`);
  };

  const createFirstDataset = (isReload: boolean = false) => {
    if (isReload) {
      dispatch(fetchDatasets() as any);
    }
  };

  useEffect(() => {
    if (dropdownBoxDisplay || showDatasetTypeDropdown) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [dropdownBoxDisplay, showDatasetTypeDropdown]);

  const showAddDatasetCard =
    isPrivatePath && workspaces?.[0]?.permissions?.can_create_dataset;

  return (
    <>
      <Helmet>
        <title>{dynamicTitle}</title>
      </Helmet>
      <Header isPrivatePath={isPrivatePath} />
      <div className={styles.datasetContainer}>
        <div className={styles.datasetCountDark}>{renderInventoryInfo()}</div>
        <div
          ref={cardContainer}
          className={
            isContentLoading || length < 1
              ? styles.cardSectionWithoutScroll
              : styles.CardSection
          }
          data-testid="dataset-inventory-cards-container"
        >
          {showAddDatasetCard ? (
            <AddDatasetCard isContentLoading={isContentLoading} />
          ) : null}

          {length < 1 && isAnyFilterSelected && !isContentLoading && (
            <NoMatchFoundCard
              onActionClick={handleClearFilters}
              isInventory={true}
              buttonText={inReview ? "Back to Review" : "Clear Filters"}
            />
          )}
          {filteredDatasets.map((dataset, i) => {
            return (
              <DatasetCard
                key={i}
                {...{
                  dataset,
                  cardElement,
                  LoadDataset,
                  deleteModalId,
                  isPrivatePath,
                  userId,
                  setCardWitdh,
                }}
              />
            );
          })}

          {count > 0 &&
            [...Array(count)].map((_, index) => (
              <div key={index} className={styles.cardPlaceHolder}>
                <div
                  className={styles.cardPlaceHolderData}
                  data-testid={
                    isContentLoading && "dataset_inventory-card-loader"
                  }
                >
                  {isContentLoading && <ContentLoad radius="33px" />}
                </div>
              </div>
            ))}
        </div>

        <CreateDatasetModal createFirstDataset={createFirstDataset} />
        <LimitExceedModal />
        <UpgradePlanModal />
        <ProPlanModal />
        <FreePlanModal />
        <SignupModal />
        <SignupSuccessModal />
        {showRequestDemoModal?.flag && (
          <RequestDemoModal calledFrom={showRequestDemoModal.calledFrom} />
        )}
        <DatasetErrorModal />
        <InternalServerErrorModal />
        <ErrorModal
          hideIcon
          showModal={showDSCreationWarning}
          title="UI Dataset Uploads Not Supported Yet"
          message={
            <div>
              At this time, creating datasets through the UI is not supported in
              On-Prem environments. This functionality will be available soon.
              <br />
              <br />
              <span className={styles.bold}>
                In the meantime, please follow the guide below to create your
                dataset.
              </span>
            </div>
          }
          onClose={() => dispatch(setShowDSCreationWarning(false))}
          actionButton1={
            <div
              className={styles.onPremGuideBtn}
              onClick={handleShowOnPremGuide}
            >
              On-Prem: Create Dataset Guide
            </div>
          }
        />
      </div>
    </>
  );
};

function mapStatesToProps(state: State) {
  return {
    datasets: getDatasets(state),
    sampleData: getSampleData(state),
    filterData: getFilterData(state),
    deleteModalId: getDeleteModalId(state),
    isContentLoading: getContentLoading(state),
    showRequestDemoModal: getShowRequestDemoModal(state),
    userId: getUserInfo(state).userId || null,
    enrichingDataset: getEnrichingDataset(state),
    showDSCreationWarning: getShowDSCreationWarning(state),
  };
}

export default connect(mapStatesToProps)(DatasetInventory);

type DatasetInventoryTypes = {
  datasets: Dataset[];
  deleteModalId: string;
  isContentLoading: boolean;
  showDSCreationWarning: boolean;
  showRequestDemoModal: { flag: boolean; calledFrom: ContactUsComponentType };
  userId: string | null;
  filterData: InventoryFilterData;
  enrichingDataset: null | EnrichingDS;
};
